import axios, { AxiosInstance, InternalAxiosRequestConfig } from "axios";
import { XMLParser } from "fast-xml-parser";
import { cardScanPackageVersion } from "../version";

import Card, {
  CaptureType,
  CardDetailsResponse,
  CardFields,
  CardState,
} from "./cardApiModel";
import { XMLErrortoCardScanError } from "./cardApiError";
import { EligibilityInfo } from "./eligibilityApiModel";
import { GlobalLogger } from "../scanview/debugLogger";

interface ListParameters {
  cursor?: string;
  limit?: number;
}

declare global {
  interface Window {
    Cypress?: any;
  }
}

interface PerformanceDataI {
  laplaceMax: number;
  laplaceVariance: number;
  modelClass: string;
  modelProbability: number;
  modelScore: number;
}

const LIVE_API_ENDPOINT = "https://api.cardscan.ai/v1/";
const SANDBOX_API_ENDPOINT = "https://sandbox.cardscan.ai/v1/";
const LIVE_WS_ENDPOINT = "wss://ws.cardscan.ai";
const SANDBOX_WS_ENDPOINT = "wss://sandbox-ws.cardscan.ai";

export default class CardScanApi {
  protected readonly instance: AxiosInstance;

  private sessionToken: string;
  private intervalId: null | ReturnType<typeof setTimeout>;
  private webviewVersion;
  private mobileSdkVersion;
  public socket: WebSocket;

  public constructor(params: {
    sessionToken: string;
    live: boolean;
    apiEndpoint?: string;
    wsEndpoint?: string;
    webviewVersion?:string;
    mobileSdkVersion?:string;
  }) {
    if (!params.sessionToken || params.sessionToken.length === 0) {
      throw new Error("sessionToken is required");
    }

    this.sessionToken = params.sessionToken;
    this.webviewVersion = params.webviewVersion;
    this.mobileSdkVersion = params.mobileSdkVersion;

    const apiEndpoint =
      params.apiEndpoint ||
      (params.live ? LIVE_API_ENDPOINT : SANDBOX_API_ENDPOINT);
    const wsEndpoint =
      params.wsEndpoint ||
      (params.live ? LIVE_WS_ENDPOINT : SANDBOX_WS_ENDPOINT);

    this.instance = axios.create({
      baseURL: apiEndpoint,
    });

    this.initializeRequestInterceptor();
    this.intervalId = null;

    let wsUrl = `${wsEndpoint}?token=${this.sessionToken}`;

    // This is a workaround for Cypress live vs stubbed tests
    if (
      window.Cypress &&
      window.Cypress.env("cardScanWebsocketUrl") &&
      window.Cypress.env("cardScanWebsocketUrl") !== "NONE"
    ) {
      wsUrl = window.Cypress.env("cardScanWebsocketUrl");
    }

    this.socket = new WebSocket(wsUrl);
  }

  private initializeRequestInterceptor = () => {
    this.instance.interceptors.request.use(this.handleRequest);
  };

  private handleRequest = (config: InternalAxiosRequestConfig) => {
    if (config && config.headers) {
      config.headers.Authorization = `Bearer ${this.sessionToken}`;
      config.headers[
        "x-cardscan-ai-sdk"
      ] = `insurance_cardscan_react  ${cardScanPackageVersion}`;
      if (this.webviewVersion){
        config.headers["x-webview-version"] = `shared_react_webview ${this.webviewVersion ?? ""}`;
      }
      if (this.mobileSdkVersion){
        config.headers["x-mobile-sdk-version"] = `sdk_version ${this.mobileSdkVersion ?? ""}`;
      }
    }
    return config;
  };

  private generateUploadUrl = (
    cardId: string,
    payload: { [key: string]: any }
  ) => {
    return this.instance.post<{
      scan_id: string;
      card_id: string;
      upload_url: string;
      upload_parameters: any;
    }>(`/cards/${cardId}/generate-upload-url`, payload);
  };

  public generateMagicLink = () => {
    return this.instance.get(`/generate-magic-link`);
  };

  public uploadCardImage = (file: File) => {
    const formData = new FormData();
    return this.instance
      .get(`/generate-upload-url`)
      .then((response) => response.data)
      .then((cardUploadRes) => {
        const upload_parameters = cardUploadRes.upload_parameters;
        for (const key in upload_parameters) {
          formData.append(key, upload_parameters[key]);
        }
        formData.append("file", file);
        return {
          uploadUrl: cardUploadRes.upload_url,
          cardId: cardUploadRes.card_id,
        };
      })
      .then(({ uploadUrl, cardId }) =>
        // needs to be axios and not this.instance because can't use authorization header.
        axios
          .post(uploadUrl, formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then(() => cardId)
          .catch((error) => {
            //errors back from S3 will be in XML.
            if (error.response && error.response.data) {
              const parser = new XMLParser();
              const errorResponse = parser.parse(error.response.data) as any;
              const cardError = XMLErrortoCardScanError(
                (errorResponse as any).Error,
                error.response.status
              );
              GlobalLogger.error(`Error uploading file: ${cardError.message}`);
              throw cardError;
            } else {
              throw error;
            }
          })
      );
  };

  public uploadScanImage = (
    scanCount: number,
    file: File,
    cardId: string,
    captureType: CaptureType,
    orientation: "front" | "back",
    metadata?: { [key: string]: any }
  ) => {
    const payload: { [k: string]: any } = {
      metadata: metadata || {},
      capture_type: captureType,
      orientation,
    };

    return this.uploadScan(cardId, file, orientation, captureType, scanCount)
      .then((response) => response.data)
      .then((data) => {
        this.setScanMetadata(data.scan_id, payload);

        return {
          scanId: data.scan_id,
          cardId: data.card_id,
        };
      });
  };

  public uploadScan = (
    cardId: string,
    file: File,
    orientation: "front" | "back",
    captureType: CaptureType,
    scanCount: number
  ) => {
    return this.instance.post(`/cards/${cardId}/upload`, file, {
      params: {
        orientation: orientation,
        capture_type: captureType,
        scanCount: scanCount,
      },
      headers: {
        "Content-Type": file.type,
      },
    });
  };

  public setScanMetadata = (
    scanId: string,
    metadata: { [key: string]: any }
  ) => {
    if (this.webviewVersion){
      metadata["webviewVersion"] = this.webviewVersion;
    }
    if (this.mobileSdkVersion){
      metadata["mobileSdkVersion"] = this.mobileSdkVersion;
    }
    return this.instance.post(`/scans/${scanId}/metadata`, metadata);
  };

  public listCards = (params?: ListParameters) => {
    const config: { [k: string]: any } = {
      params: {},
    };

    if (params?.cursor) config.params.cursor = params?.cursor;
    if (params?.limit) config.params.limit = params?.limit;

    return this.instance.get<{
      cards: CardDetailsResponse[];
      response_metadata: { limit: number; next_cursor: string; total: number };
    }>("/cards", config);
  };

  public getCard = (cardId: string) => {
    return this.instance.get<CardDetailsResponse>(`/cards/${cardId}`);
  };

  public createEligibility = (
    eligibility: EligibilityInfo,
    card_id: string
  ) => {
    const payload = {
      eligibility,
      card_id,
    };
    return this.instance.post(`/eligibility`, payload);
  };

  public getEligibility = (eligibilityId: string) => {
    return this.instance.get(`eligibility/${eligibilityId}`);
  };

  public getCardImage = (cardId: string) => {
    return this.instance.get(`/cards/${cardId}/image`);
  };

  public createCard = (
    backsideSupport?: boolean,
    enableLivescan?: boolean,
    metadata?: { [key: string]: any }
  ) => {
    const payload = {
      enable_backside_scan: backsideSupport,
      enable_livescan: enableLivescan,
      ...(metadata && { metadata: metadata }),
    };
    return this.instance.post<CardDetailsResponse>("/cards", payload);
  };

  public performanceData = (cardId: string, data: PerformanceDataI) => {
    return this.instance.post(`/cards/${cardId}/performance`, {
      ...data,
      timestamp: new Date().toISOString(),
    });
  };

  public subscribe = (cardId: string, callback: (x: Card) => void) => {
    let inProgress = false;
    let isDifferent = true;
    let lastState = "";
    this.intervalId = setInterval(() => {
      if (!inProgress) {
        inProgress = true;
        this.getCard(cardId)
          .then((res) => {
            if (lastState !== res.data.state) {
              isDifferent = true;
            }
            if (isDifferent) {
              lastState = res.data.state;
              callback(new Card(res.data));
            }
            inProgress = false;
            if (["completed", "error"].includes(res.data.state))
              this.cancelSubscription();
          })
          .catch((err) => {
            GlobalLogger.error(`Error getting card: ${cardId}`);
            inProgress = false;
            callback(
              new Card({
                state: CardState.error,
                message: err.response.data.message,
                card_id: cardId,
              })
            );
            this.cancelSubscription();
          });
      }
    }, 1000);
  };

  public cancelSubscription = () => {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  };
}
